import { useTranslation } from 'react-i18next';
import { buildPath, parsePath, type Route } from '../routing';
import { normalizeLanguage } from '../i18n/utils';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useEffect, useState } from 'react';
import { Menu, X, ShoppingCart } from 'lucide-react';

export function Header() {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLanguage(i18n.language);
  const homePath = buildPath(currentLang, 'home');
  const featuresPath = buildPath(currentLang, 'features');
  const pricingPath = buildPath(currentLang, 'pricing');
  const downloadPath = buildPath(currentLang, 'download');
  const contactPath = buildPath(currentLang, 'contact');
  const legalPath = buildPath(currentLang, 'terms');
  const buyNowHref = pricingPath;
  const [activeRoute, setActiveRoute] = useState<Route>('home');
  const [activeHash, setActiveHash] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateActive = () => {
      const { route } = parsePath(window.location.pathname);
      setActiveRoute(route);
      setActiveHash(window.location.hash || '');
    };
    updateActive();
    window.addEventListener('hashchange', updateActive);
    window.addEventListener('popstate', updateActive);
    return () => {
      window.removeEventListener('hashchange', updateActive);
      window.removeEventListener('popstate', updateActive);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMenuOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActive = (key: 'home' | 'features' | 'pricing' | 'download' | 'contact' | 'legal') => {
    if (key === 'download') return activeRoute === 'download';
    if (key === 'contact') return activeRoute === 'contact';
    if (key === 'legal') return activeRoute === 'terms' || activeRoute === 'privacy';
    if (key === 'features') return activeRoute === 'features' || (activeRoute === 'home' && activeHash === '#features');
    if (key === 'pricing') return activeRoute === 'pricing' || (activeRoute === 'home' && activeHash === '#pricing');
    return activeRoute === 'home' && activeHash === '';
  };

  const navItemClass = (active: boolean) =>
    [
      'font-medium',
      'px-3',
      'py-2',
      'transition-colors',
      active ? 'text-teal-600' : 'text-gray-700 hover:text-teal-400',
    ].join(' ');

  const navItemStyle = (active: boolean) => ({
    backgroundImage: active ? 'linear-gradient(rgba(13, 148, 136, 0.55), rgba(13, 148, 136, 0.55))' : 'none',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center bottom',
    backgroundSize: active ? '95% 2px' : '0 0',
    paddingBottom: '2px',
  });

  return (
    <header
      className="bg-white shadow-lg"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center">
          <a
            href={homePath}
            className="font-bold text-gray-900 text-2xl inline-flex items-center gap-2"
            style={{ paddingRight: '8px' }}
          >
            <img
              src="/favicon.svg"
              alt="Noteon logo"
              className="rounded-md"
              style={{ width: '28px', height: '28px' }}
              loading="eager"
            />
            <span>Noteon</span>
          </a>
          {!isMobile && (
            <>
              <span
                aria-hidden="true"
                style={{
                  width: '1px',
                  height: '24px',
                  backgroundColor: '#e5e7eb',
                  margin: '0 32px',
                }}
              />
              <nav className="flex flex-wrap items-center gap-4 text-lg" style={{ paddingLeft: '8px' }}>
                <a href={homePath} className={navItemClass(isActive('home'))} style={navItemStyle(isActive('home'))}>
                  {t('nav.home')}
                </a>
                <a
                  href={featuresPath}
                  className={navItemClass(isActive('features'))}
                  style={navItemStyle(isActive('features'))}
                >
                  {t('nav.features')}
                </a>
                <a
                  href={buyNowHref}
                  className={[
                    navItemClass(isActive('pricing')),
                    'inline-flex',
                    'items-center',
                    'gap-2',
                    'font-semibold',
                  ].join(' ')}
                  style={navItemStyle(isActive('pricing'))}
                >
                  <ShoppingCart className="size-4 text-teal-600" />
                  {t('nav.pricing')}
                </a>
                <a
                  href={downloadPath}
                  className={navItemClass(isActive('download'))}
                  style={navItemStyle(isActive('download'))}
                >
                  {t('nav.download')}
                </a>
                <a
                  href={legalPath}
                  className={navItemClass(isActive('legal'))}
                  style={navItemStyle(isActive('legal'))}
                >
                  {t('nav.legal')}
                </a>
                <a
                  href={contactPath}
                  className={navItemClass(isActive('contact'))}
                  style={navItemStyle(isActive('contact'))}
                >
                  {t('nav.contact')}
                </a>
              </nav>
            </>
          )}
        </div>
        {isMobile ? (
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-2 rounded-lg border border-gray-200 text-gray-700"
              aria-expanded={menuOpen}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
          </div>
        ) : (
          <LanguageSwitcher />
        )}
      </div>
      {isMobile && menuOpen && (
        <div className="border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex flex-col gap-2 text-lg">
              <a
                href={homePath}
                className={navItemClass(isActive('home'))}
                style={navItemStyle(isActive('home'))}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.home')}
              </a>
              <a
                href={featuresPath}
                className={navItemClass(isActive('features'))}
                style={navItemStyle(isActive('features'))}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.features')}
              </a>
              <a
                href={buyNowHref}
                className={[
                  navItemClass(isActive('pricing')),
                  'inline-flex',
                  'items-center',
                  'gap-2',
                  'font-semibold',
                ].join(' ')}
                style={navItemStyle(isActive('pricing'))}
                onClick={() => setMenuOpen(false)}
              >
                <ShoppingCart className="size-4 text-teal-600" />
                {t('nav.pricing')}
              </a>
              <a
                href={downloadPath}
                className={navItemClass(isActive('download'))}
                style={navItemStyle(isActive('download'))}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.download')}
              </a>
              <a
                href={legalPath}
                className={navItemClass(isActive('legal'))}
                style={navItemStyle(isActive('legal'))}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.legal')}
              </a>
              <a
                href={contactPath}
                className={navItemClass(isActive('contact'))}
                style={navItemStyle(isActive('contact'))}
                onClick={() => setMenuOpen(false)}
              >
                {t('nav.contact')}
              </a>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}
